import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, InsurancePolicy } from '../../lib/supabase';
import { DocumentUpload } from './DocumentUpload';
import { getExpirationStatus } from '../../lib/documentService';
import {
  Plus,
  Shield,
  Calendar,
  DollarSign,
  FileText,
  ExternalLink,
  Trash2,
  Edit2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Building2
} from 'lucide-react';

export const InsuranceManager: React.FC = () => {
  const { profile } = useAuth();
  const [policies, setPolicies] = useState<InsurancePolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    policy_type: 'liability' as 'liability' | 'workers_comp' | 'tools',
    provider: '',
    policy_number: '',
    coverage_amount: '',
    start_date: '',
    expiry_date: '',
    document_url: ''
  });

  useEffect(() => {
    loadPolicies();
  }, [profile]);

  const loadPolicies = async () => {
    if (!profile) return;

    try {
      const { data, error } = await supabase
        .from('insurance_policies')
        .select('*')
        .eq('user_id', profile.id)
        .order('expiry_date', { ascending: true });

      if (error) throw error;
      setPolicies(data || []);
    } catch (error) {
      console.error('Error loading policies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    try {
      const policyData = {
        ...formData,
        coverage_amount: formData.coverage_amount ? parseFloat(formData.coverage_amount) : null,
        status: 'active' as const
      };

      if (editingId) {
        const { error } = await supabase
          .from('insurance_policies')
          .update(policyData)
          .eq('id', editingId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('insurance_policies')
          .insert({
            user_id: profile.id,
            ...policyData
          });

        if (error) throw error;
      }

      resetForm();
      await loadPolicies();
    } catch (error) {
      console.error('Error saving policy:', error);
      alert('Failed to save insurance policy');
    }
  };

  const handleEdit = (policy: InsurancePolicy) => {
    setFormData({
      policy_type: policy.policy_type,
      provider: policy.provider,
      policy_number: policy.policy_number || '',
      coverage_amount: policy.coverage_amount?.toString() || '',
      start_date: policy.start_date,
      expiry_date: policy.expiry_date,
      document_url: policy.document_url || ''
    });
    setEditingId(policy.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this insurance policy?')) return;

    try {
      const { error } = await supabase
        .from('insurance_policies')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadPolicies();
    } catch (error) {
      console.error('Error deleting policy:', error);
      alert('Failed to delete policy');
    }
  };

  const handleUploadComplete = async (url: string, policyId?: string) => {
    if (policyId) {
      try {
        const { error } = await supabase
          .from('insurance_policies')
          .update({ document_url: url })
          .eq('id', policyId);

        if (error) throw error;
        setUploadingFor(null);
        await loadPolicies();
      } catch (error) {
        console.error('Error updating document URL:', error);
      }
    } else {
      setFormData(prev => ({ ...prev, document_url: url }));
    }
  };

  const resetForm = () => {
    setFormData({
      policy_type: 'liability',
      provider: '',
      policy_number: '',
      coverage_amount: '',
      start_date: '',
      expiry_date: '',
      document_url: ''
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const getPolicyTypeLabel = (type: string) => {
    switch (type) {
      case 'liability':
        return 'General Liability';
      case 'workers_comp':
        return "Workers' Compensation";
      case 'tools':
        return 'Tools & Equipment';
      default:
        return type;
    }
  };

  const getPolicyTypeColor = (type: string) => {
    switch (type) {
      case 'liability':
        return 'bg-blue-100 text-blue-700';
      case 'workers_comp':
        return 'bg-green-100 text-green-700';
      case 'tools':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Insurance Policies</h2>
          <p className="text-slate-600 mt-1">Manage your insurance coverage</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg transition-all shadow-md hover:shadow-lg"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Policy
        </button>
      </div>

      {showAddForm && (
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl border border-blue-200 p-6 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">
              {editingId ? 'Edit Policy' : 'Add New Policy'}
            </h3>
            <button
              onClick={resetForm}
              className="text-slate-400 hover:text-slate-600"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Policy Type *
                </label>
                <select
                  value={formData.policy_type}
                  onChange={(e) => setFormData({ ...formData, policy_type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                  required
                >
                  <option value="liability">General Liability</option>
                  <option value="workers_comp">Workers' Compensation</option>
                  <option value="tools">Tools & Equipment</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Insurance Provider *
                </label>
                <input
                  type="text"
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                  placeholder="e.g., Next Insurance, The Hartford"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Policy Number
                </label>
                <input
                  type="text"
                  value={formData.policy_number}
                  onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                  placeholder="Policy ID"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Coverage Amount ($)
                </label>
                <input
                  type="number"
                  value={formData.coverage_amount}
                  onChange={(e) => setFormData({ ...formData, coverage_amount: e.target.value })}
                  placeholder="e.g., 1000000"
                  min="0"
                  step="1000"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Expiration Date *
                </label>
                <input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                  required
                />
              </div>
            </div>

            {!formData.document_url && profile && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Upload Certificate of Insurance (Optional)
                </label>
                <DocumentUpload
                  userId={profile.id}
                  documentType="insurance"
                  onUploadComplete={(url) => handleUploadComplete(url)}
                />
              </div>
            )}

            {formData.document_url && (
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-700">Document uploaded</span>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, document_url: '' })}
                  className="text-sm text-green-600 hover:text-green-700"
                >
                  Remove
                </button>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg transition-all shadow-md hover:shadow-lg"
              >
                {editingId ? 'Update' : 'Add'} Policy
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2.5 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {policies.map((policy) => {
          const expiryStatus = getExpirationStatus(policy.expiry_date);

          return (
            <div key={policy.id} className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-blue-300 transition-all hover:scale-[1.01]">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
                    <Shield className="w-6 h-6 text-white" />
                  </div>

                  <div className="flex-1">
                    <h3 className="font-bold text-slate-900 mb-1">
                      {getPolicyTypeLabel(policy.policy_type)}
                    </h3>

                    <div className="flex items-center space-x-2 text-sm text-slate-600 mb-2">
                      <Building2 className="w-4 h-4" />
                      <span>{policy.provider}</span>
                    </div>

                    <div className="flex flex-wrap gap-3 text-sm text-slate-600 mb-3">
                      {policy.policy_number && (
                        <span className="flex items-center">
                          <FileText className="w-4 h-4 mr-1" />
                          {policy.policy_number}
                        </span>
                      )}
                      {policy.coverage_amount && (
                        <span className="flex items-center">
                          <DollarSign className="w-4 h-4 mr-1" />
                          ${policy.coverage_amount.toLocaleString()} coverage
                        </span>
                      )}
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(policy.start_date).toLocaleDateString()} - {new Date(policy.expiry_date).toLocaleDateString()}
                      </span>
                    </div>

                    <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      expiryStatus.status === 'expired'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : expiryStatus.status === 'expiring'
                        ? 'bg-amber-50 text-amber-700 border border-amber-200'
                        : 'bg-green-50 text-green-700 border border-green-200'
                    }`}>
                      {expiryStatus.status === 'expired' ? (
                        <XCircle className="w-3 h-3 mr-1" />
                      ) : expiryStatus.status === 'expiring' ? (
                        <AlertTriangle className="w-3 h-3 mr-1" />
                      ) : (
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                      )}
                      {expiryStatus.message}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {policy.document_url ? (
                    <a
                      href={policy.document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="View certificate"
                    >
                      <ExternalLink className="w-4 h-4 text-slate-600" />
                    </a>
                  ) : (
                    <button
                      onClick={() => setUploadingFor(policy.id)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Upload certificate"
                    >
                      <FileText className="w-4 h-4 text-slate-600" />
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(policy)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4 text-slate-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(policy.id)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>

              {uploadingFor === policy.id && profile && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <DocumentUpload
                    userId={profile.id}
                    documentType="insurance"
                    onUploadComplete={(url) => handleUploadComplete(url, policy.id)}
                    onCancel={() => setUploadingFor(null)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {policies.length === 0 && !showAddForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h3 className="font-bold text-slate-900 mb-2">No insurance policies yet</h3>
          <p className="text-slate-600 mb-4">
            Add your insurance coverage to qualify for more jobs
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg transition-all shadow-md hover:shadow-lg"
          >
            Add First Policy
          </button>
        </div>
      )}
    </div>
  );
};
